export function DueDiligence() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Managing the Due Diligence Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The due diligence period is when most apartment building sales fall apart. Understanding how to 
            manage this critical phase is among the most important tips for selling an apartment building 
            successfully. Proper preparation and responsiveness during due diligence keeps deals on track and 
            prevents buyer remorse or renegotiation attempts.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/661b9cb5b97288c657dd777081d256ed.jpg"
              alt="Due diligence inspection"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Buyers Investigate During Due Diligence</h3>
            <p className="text-gray-600 mb-6">
              Understanding what buyers examine during due diligence helps you prepare in advance and respond 
              effectively to requests. Buyers typically spend 30-45 days investigating every aspect of your 
              property to verify information and identify potential issues. Being prepared for these inquiries 
              is essential for smooth transactions.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-file-chart-line text-green-600 mr-2"></i>
                  Financial Due Diligence
                </h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Verification of income and expense statements</li>
                  <li>• Review of rent rolls and lease agreements</li>
                  <li>• Analysis of historical financial performance</li>
                  <li>• Examination of security deposit accounting</li>
                  <li>• Review of property tax assessments and appeals</li>
                  <li>• Verification of utility expenses and allocations</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-building-line text-orange-600 mr-2"></i>
                  Physical Due Diligence
                </h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Comprehensive property inspections</li>
                  <li>• Roof, structural, and foundation assessments</li>
                  <li>• HVAC, plumbing, and electrical system evaluations</li>
                  <li>• Environmental assessments (Phase I/II)</li>
                  <li>• Survey and boundary verification</li>
                  <li>• ADA compliance review</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-shield-check-line text-red-600 mr-2"></i>
                  Legal Due Diligence
                </h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Title examination and exception review</li>
                  <li>• Zoning compliance verification</li>
                  <li>• Review of permits and certificates of occupancy</li>
                  <li>• Examination of easements and encumbrances</li>
                  <li>• Review of any pending litigation</li>
                  <li>• Verification of property boundaries</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Preparing for Due Diligence Success
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Proactive preparation is one of the most effective tips for selling an apartment building smoothly. 
            By anticipating buyer requests and organizing information in advance, you demonstrate professionalism, 
            build buyer confidence, and significantly reduce the risk of deal complications or renegotiations.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-folder-line text-orange-600 mr-2"></i>
                Create a Due Diligence Package
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                Prepare a comprehensive due diligence package before listing your property. This organized 
                collection of documents demonstrates professionalism and allows buyers to move quickly through 
                their review process.
              </p>
              <div className="space-y-3">
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Financial Documents</h5>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• 3 years of profit and loss statements</li>
                    <li>• Current year-to-date financials</li>
                    <li>• Detailed rent roll with lease terms</li>
                    <li>• Historical occupancy reports</li>
                    <li>• Utility bills and expense documentation</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Property Documents</h5>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• All lease agreements and amendments</li>
                    <li>• Property survey and legal description</li>
                    <li>• Title insurance policy</li>
                    <li>• Recent inspection reports</li>
                    <li>• Warranties and service contracts</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Operational Documents</h5>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Vendor contracts and service agreements</li>
                    <li>• Property management agreements</li>
                    <li>• Insurance policies and claims history</li>
                    <li>• Capital improvement records</li>
                    <li>• Maintenance logs and schedules</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-search-eye-line text-green-600 mr-2"></i>
                Conduct Pre-Listing Inspections
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                One of the smartest tips for selling an apartment building is conducting your own inspections 
                before listing. This allows you to identify and address issues proactively rather than being 
                surprised during buyer due diligence.
              </p>
              <div className="space-y-3">
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Benefits of Pre-Listing Inspections</h5>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Identify issues before buyers discover them</li>
                    <li>• Control the narrative around property condition</li>
                    <li>• Fix problems at your pace and cost</li>
                    <li>• Reduce renegotiation leverage for buyers</li>
                    <li>• Demonstrate transparency and build trust</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Key Inspections to Consider</h5>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Comprehensive property inspection</li>
                    <li>• Roof certification and assessment</li>
                    <li>• Phase I environmental assessment</li>
                    <li>• HVAC system evaluation</li>
                    <li>• Plumbing and electrical review</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Strategic Disclosure</h5>
                  <p className="text-gray-600 text-xs">
                    Disclose known issues upfront with your inspection reports. This builds buyer confidence 
                    and prevents surprises that could derail your deal. Buyers appreciate transparency and 
                    are more likely to accept disclosed issues than discover problems themselves.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Responding to Due Diligence Requests</h3>
            <p className="text-gray-600 mb-6">
              How you respond to buyer requests during due diligence significantly impacts deal success. Slow 
              responses or incomplete information create doubt and give buyers leverage to renegotiate. These 
              tips for selling an apartment building during due diligence help maintain momentum and buyer 
              confidence throughout the process.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Respond Quickly and Completely</h4>
                <p className="text-gray-600 text-sm">
                  Answer all buyer requests within 24-48 hours. Quick, thorough responses demonstrate 
                  professionalism and keep deals moving forward. Delays create doubt and give buyers time 
                  to reconsider or find problems. Assign someone to manage due diligence requests full-time 
                  if necessary.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2">Be Transparent and Honest</h4>
                <p className="text-gray-600 text-sm">
                  Never hide problems or provide misleading information. Buyers will discover issues during 
                  inspections, and dishonesty destroys trust and often kills deals. If you don't know an 
                  answer, say so and commit to finding out. Transparency builds confidence and prevents 
                  post-closing liability.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2">Facilitate Property Access</h4>
                <p className="text-gray-600 text-sm">
                  Make your property easily accessible for inspections while respecting tenant rights. 
                  Coordinate inspection schedules efficiently and provide access to all areas including 
                  mechanical rooms, roofs, and vacant units. Difficult access frustrates buyers and their 
                  inspectors, creating negative impressions.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2">Address Issues Proactively</h4>
                <p className="text-gray-600 text-sm">
                  When inspections reveal problems, address them immediately. Decide whether to fix issues, 
                  offer credits, or adjust the price. Proactive problem-solving prevents buyers from using 
                  issues as leverage for major renegotiations. Show willingness to work with buyers while 
                  protecting your interests.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=real%20estate%20transaction%20coordinator%20managing%20due%20diligence%20documents%20organized%20files%20and%20paperwork%20professional%20property%20manager%20responding%20to%20buyer%20requests%20efficient%20document%20management%20commercial%20real%20estate%20closing%20process&width=800&height=600&seq=tips-diligence-002&orientation=landscape"
              alt="Managing due diligence documents"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}